import * as React from 'react';
import { FormEvent, useState } from 'react';
import { FormControl, Button, Well } from 'react-bootstrap';
import { ICenterBase } from '@nkr/models/index';
import { OrganType } from '@nkr/enums/index';

export type AdvancedFindFields = {
    name: string,
    alias: string,
    email: string,
    center: number,
    organType: number,
    intlDonor: number,
    donationDateFrom: string,
    donationDateTo: string,
    birthDate: string,
};

export type AdvancedFindChip = {
    key: string,
    label: string,
};

interface IProps {
    mode: 'subset' | 'systemSearch',
    find: AdvancedFindFields,
    isAdmin: boolean,
    centerList: ICenterBase[],
    deepSearchActive: boolean,
    chips: AdvancedFindChip[],
    deepFirstName: string,
    deepLastName: string,
    deepEmail: string,
    deepBirthDate: string,
    deepAlias: string,
    deepCenter: number,
    deepStartDate: string,
    deepEndDate: string,
    onFindChange: (property: keyof AdvancedFindFields, value: string | number) => void,
    onClearFindField: (key: string) => void,
    onClearAllFind: () => void,
    onExitDeepSearch: () => void,
    onDeepFieldChange: (property: string, value: any) => void,
    onDeepSearch: () => void,
    onDeepReset: () => void,
}

const fieldWrap: React.CSSProperties = {
    marginRight: 20,
    marginBottom: 8,
};

const labelStyle: React.CSSProperties = {
    marginRight: 5,
};

const chipsRow: React.CSSProperties = {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 8,
    marginTop: 8,
};

const chipStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '4px 8px',
    borderRadius: 4,
    background: '#f5f5f5',
    border: '1px solid #ddd',
    color: '#333',
    fontSize: 13,
    fontWeight: 600,
    lineHeight: 1.2,
    cursor: 'pointer',
};

const AdvancedFindBar = (props: IProps) => {
    const hasFind =
        !!(props.find.name || props.find.alias || props.find.email || props.find.center > 0
            || props.find.organType >= 0 || props.find.intlDonor >= 0
            || props.find.donationDateFrom || props.find.donationDateTo || props.find.birthDate);
    const [advancedOpen, setAdvancedOpen] = useState(
        !!(props.find.organType >= 0 || props.find.intlDonor >= 0
            || props.find.donationDateFrom || props.find.donationDateTo || props.find.birthDate)
    );

    const handleFindChange = (e: FormEvent<HTMLInputElement & HTMLSelectElement & FormControl>) => {
        const { name, value } = e.currentTarget;
        if (name === 'center' || name === 'organType' || name === 'intlDonor') {
            props.onFindChange(name as keyof AdvancedFindFields, parseInt(value, 10));
            return;
        }
        props.onFindChange(name as keyof AdvancedFindFields, value);
    };

    const handleDeepChange = (e: FormEvent<HTMLInputElement & HTMLSelectElement & FormControl>) => {
        const { name, value } = e.currentTarget;
        props.onDeepFieldChange(name, name === 'center' ? parseInt(value, 10) || 0 : value);
    };

    const onDeepKeyDown = (event: React.KeyboardEvent<FormControl>) => {
        if (event.key === 'Enter') {
            props.onDeepSearch();
        }
    };

    const renderField = (label: string, control: React.ReactNode, key: string) => (
        <div key={key} style={fieldWrap}>
            <div style={labelStyle}>{label}</div>
            <div>{control}</div>
        </div>
    );

    const renderChips = () => {
        if (props.chips.length === 0) {
            return null;
        }
        return (
            <div style={chipsRow}>
                {props.chips.map((chip) => (
                    <button
                        key={chip.key}
                        type="button"
                        style={chipStyle}
                        onClick={() => props.onClearFindField(chip.key)}
                        title="Remove filter"
                    >
                        <span>{chip.label}</span>
                        <span aria-hidden>×</span>
                    </button>
                ))}
            </div>
        );
    };

    if (props.mode === 'systemSearch') {
        return (
            <Well bsSize="sm" style={{ marginBottom: 5 }}>
                <div style={{ marginBottom: 8, color: '#555' }}>
                    {props.deepSearchActive
                        ? 'Showing system search results across Advanced cases.'
                        : 'Search across all Advanced cases on the server.'}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', width: '100%', alignItems: 'center' }}>
                    {props.isAdmin && renderField(
                        'Filter by Center',
                        <FormControl
                            componentClass="select"
                            name="center"
                            value={props.deepCenter}
                            onChange={handleDeepChange}
                        >
                            <option value={0}>- All Centers -</option>
                            {props.centerList.map((center) => (
                                <option key={'adv-deep-ctr-' + center.id} value={center.id}>{center.alias}</option>
                            ))}
                        </FormControl>,
                        'center',
                    )}
                    {renderField(
                        'First Name',
                        <FormControl name="firstName" value={props.deepFirstName} onKeyDown={onDeepKeyDown} onChange={handleDeepChange} />,
                        'firstName',
                    )}
                    {renderField(
                        'Last Name',
                        <FormControl name="lastName" value={props.deepLastName} onKeyDown={onDeepKeyDown} onChange={handleDeepChange} />,
                        'lastName',
                    )}
                    {renderField(
                        'Email:',
                        <FormControl type="email" name="email" value={props.deepEmail} onKeyDown={onDeepKeyDown} onChange={handleDeepChange} />,
                        'email',
                    )}
                    {renderField(
                        'Alias:',
                        <FormControl name="alias" value={props.deepAlias} onKeyDown={onDeepKeyDown} onChange={handleDeepChange} />,
                        'alias',
                    )}
                    {renderField(
                        'Birth Date:',
                        <FormControl type="date" name="birthDate" value={props.deepBirthDate} onKeyDown={onDeepKeyDown} onChange={handleDeepChange} />,
                        'birthDate',
                    )}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', width: '100%', marginTop: 10, alignItems: 'center' }}>
                    {renderField(
                        'DS Reg Start Date',
                        <FormControl type="date" name="startDate" value={props.deepStartDate} onChange={handleDeepChange} />,
                        'startDate',
                    )}
                    {renderField(
                        'DS Reg End Date',
                        <FormControl type="date" name="endDate" value={props.deepEndDate} onChange={handleDeepChange} />,
                        'endDate',
                    )}
                    <div style={{ marginLeft: 50, marginTop: 19 }}>
                        <Button bsStyle="primary" onClick={props.onDeepSearch}>Filter</Button>&nbsp;
                        <Button onClick={props.onDeepReset}>Reset Filters</Button>
                        {props.deepSearchActive && (
                            <>
                                &nbsp;
                                <Button onClick={props.onExitDeepSearch}>Exit results</Button>
                            </>
                        )}
                    </div>
                </div>
                {renderChips()}
            </Well>
        );
    }

    return (
        <Well bsSize="sm" style={{ marginBottom: 5 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', width: '100%', alignItems: 'center' }}>
                {renderField(
                    'Name',
                    <FormControl
                        name="name"
                        value={props.find.name}
                        onChange={handleFindChange}
                    />,
                    'name',
                )}
                {renderField(
                    'Alias:',
                    <FormControl
                        name="alias"
                        value={props.find.alias}
                        onChange={handleFindChange}
                    />,
                    'alias',
                )}
                {renderField(
                    'Email:',
                    <FormControl
                        name="email"
                        type="email"
                        value={props.find.email}
                        onChange={handleFindChange}
                    />,
                    'email',
                )}
                {props.isAdmin && renderField(
                    'Filter by Center',
                    <FormControl
                        componentClass="select"
                        name="center"
                        value={props.find.center}
                        onChange={handleFindChange}
                    >
                        <option value={0}>- All Centers -</option>
                        {props.centerList.map((center) => (
                            <option key={'adv-find-ctr-' + center.id} value={center.id}>{center.alias}</option>
                        ))}
                    </FormControl>,
                    'center',
                )}
                <div style={{ marginLeft: 30, marginTop: 19 }}>
                    <Button
                        bsStyle={advancedOpen ? 'primary' : 'default'}
                        onClick={() => setAdvancedOpen(!advancedOpen)}
                    >
                        {advancedOpen ? 'Hide advanced' : 'Advanced'}
                    </Button>
                    {hasFind && (
                        <>
                            &nbsp;
                            <Button onClick={props.onClearAllFind}>Clear</Button>
                        </>
                    )}
                </div>
            </div>

            {advancedOpen && (
                <div style={{ display: 'flex', flexWrap: 'wrap', width: '100%', marginTop: 10, alignItems: 'center' }}>
                    {renderField(
                        'Organ Type',
                        <FormControl
                            componentClass="select"
                            name="organType"
                            value={props.find.organType}
                            onChange={handleFindChange}
                        >
                            <option value={-1}>- All -</option>
                            <option value={OrganType.Kidney}>Kidney</option>
                            <option value={OrganType.Liver}>Liver</option>
                        </FormControl>,
                        'organType',
                    )}
                    {renderField(
                        'Intl Donor',
                        <FormControl
                            componentClass="select"
                            name="intlDonor"
                            value={props.find.intlDonor}
                            onChange={handleFindChange}
                        >
                            <option value={-1}>- Any -</option>
                            <option value={1}>Yes</option>
                            <option value={0}>No</option>
                        </FormControl>,
                        'intlDonor',
                    )}
                    {renderField(
                        'Donation From',
                        <FormControl
                            name="donationDateFrom"
                            type="date"
                            value={props.find.donationDateFrom}
                            onChange={handleFindChange}
                        />,
                        'donationDateFrom',
                    )}
                    {renderField(
                        'Donation To',
                        <FormControl
                            name="donationDateTo"
                            type="date"
                            value={props.find.donationDateTo}
                            onChange={handleFindChange}
                        />,
                        'donationDateTo',
                    )}
                    {renderField(
                        'Birth Date:',
                        <FormControl
                            name="birthDate"
                            type="date"
                            value={props.find.birthDate}
                            onChange={handleFindChange}
                        />,
                        'birthDate',
                    )}
                </div>
            )}
            {renderChips()}
        </Well>
    );
};

export default AdvancedFindBar;
